import { Instagram, Send, Mail } from "lucide-react";
import { SOCIALS } from "@/data/mock";
import { useSiteConfig } from "@/context/SiteConfig";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { AppDownload } from "@/components/AppDownload";

const LINKS = [
  { label: "Início", href: "#inicio" },
  { label: "Sinais Ao Vivo", href: "#rtp" },
  { label: "Jogos", href: "#jogos" },
  { label: "Ranking", href: "#ranking" },
  { label: "Sinais", href: "#sinais" },
  { label: "Estatísticas", href: "#estatisticas" },
];

export const Footer = () => {
  const cfg = useSiteConfig();
  const go = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer
      id="contato"
      className="relative border-t border-white/[0.08] pt-16 pb-8 overflow-hidden"
      data-testid="site-footer"
    >
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] radial-gold blur-3xl opacity-40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <AppDownload />

        <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-10 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 radial-gold scale-150 blur-md" />
                <img
                  src="/manga-symbol.png"
                  alt="Grupo Manga"
                  className="relative w-12 h-12 object-contain gold-glow"
                />
              </div>
              <div>
                <div className="font-display text-lg font-extrabold text-white">
                  GRUPO <span className="text-gold-gradient">MANGA</span>
                </div>
                <div className="text-[10px] tracking-[0.2em] text-white/40 uppercase">
                  Sinais • Análises • Plataformas
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/50 max-w-xs leading-relaxed">
              Plataforma informativa de monitoramento de sinais. Todo o conteúdo é
              informativo e não garante resultados.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a
                href={cfg.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-[#F5C542] hover:border-[#F5C542]/50 transition-colors duration-300"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={cfg.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-[#F5C542] hover:border-[#F5C542]/50 transition-colors duration-300"
                aria-label="Telegram"
                data-testid="footer-telegram"
              >
                <Send className="w-5 h-5" />
              </a>
              {cfg.whatsapp && (
                <a
                  href={cfg.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white/70 hover:text-[#25D366] hover:border-[#25D366]/50 transition-colors duration-300"
                  aria-label="WhatsApp"
                  data-testid="footer-whatsapp"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Links */}
          <div>
            <div className="font-display text-sm font-bold uppercase tracking-wider text-white mb-4">
              Links Rápidos
            </div>
            <ul className="space-y-3">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => go(l.href)}
                    className="text-sm text-white/50 hover:text-[#F5C542] transition-colors duration-300"
                    data-testid={`footer-link-${l.href.replace("#", "")}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="font-display text-sm font-bold uppercase tracking-wider text-white mb-4">
              Contato
            </div>
            <a
              href={`mailto:${cfg.email}`}
              className="flex items-center gap-2 text-sm text-white/50 hover:text-[#F5C542] transition-colors duration-300"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              {cfg.email}
            </a>
            <a
              href={cfg.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-white/50 hover:text-[#F5C542] transition-colors duration-300 mt-3"
            >
              <Send className="w-4 h-4" />
              Grupo no Telegram
            </a>
          </div>
        </div>

        <div className="pt-8 border-t border-white/[0.08] flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span data-testid="footer-copyright">
            {cfg.copyright}
            {cfg.cnpj && (
              <span className="block mt-1 text-white/30">CNPJ: {cfg.cnpj}</span>
            )}
          </span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-center md:justify-end">
            <a href="/termos-de-uso" className="hover:text-[#F5C542] transition-colors" data-testid="footer-terms">
              Termos de Uso
            </a>
            <a href="/politica-de-privacidade" className="hover:text-[#F5C542] transition-colors" data-testid="footer-privacy">
              Política de Privacidade
            </a>
            <span>+18 • Conteúdo exclusivamente informativo.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
